import type { LogLevel } from '../types';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
  data?: any;
}

class Logger {
  private static instance: Logger;
  private isProd = import.meta.env.PROD;
  private logBuffer: LogEntry[] = [];
  private readonly bufferSize = 100;
  private readonly flushInterval = 60000; // 1 minute

  private constructor() {
    setInterval(() => this.flush(), this.flushInterval);
    window.addEventListener('unload', () => this.flush());
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${
      data ? ` ${JSON.stringify(data)}` : ''
    }`;
  }

  private log(level: LogLevel, message: string, data?: any) {
    const entry: LogEntry = {
      level,
      message,
      timestamp: Date.now(),
      data
    };

    // Add to buffer
    this.logBuffer.push(entry);
    if (this.logBuffer.length >= this.bufferSize) {
      this.flush();
    }

    // Always log errors and non-prod logs to console
    if (level === 'error' || !this.isProd) {
      const formattedMessage = this.formatMessage(level, message, data);
      console[level](formattedMessage);
    }
  }

  private async flush() {
    if (this.logBuffer.length === 0) return;

    if (this.isProd) {
      try {
        // Group logs by level
        const groupedLogs = this.logBuffer.reduce((acc, log) => {
          acc[log.level] = acc[log.level] || [];
          acc[log.level].push(log);
          return acc;
        }, {} as Record<LogLevel, LogEntry[]>);

        // Send critical logs immediately
        if (groupedLogs.error?.length) {
          console.error('Critical logs:', groupedLogs.error);
        }
      } catch (error) {
        console.error('Failed to send logs:', error);
      }
    }

    this.logBuffer = [];
  }

  public debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  public info(message: string, data?: any) {
    this.log('info', message, data);
  }

  public warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  public error(message: string, error?: Error | any) {
    this.log('error', message, {
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : error
    });
  }
}

export const logger = Logger.getInstance();